import React, {FC, useMemo} from 'react';
import styled from 'styled-components';
import Box from '@material-ui/core/Box';
import Card from '@material-ui/core/Card';
import Typography from '@material-ui/core/Typography';
import {CardProps, withStyles} from '@material-ui/core';

import {getLocale} from '@epic-core/common/dist';
import {Guideline} from '@epic-ui/icons';
import {useComparisonColor} from '@epic-visualizations/theme';

import useLocalization from '../../../../../hooks/useLocalization';
import MyTop10GamesPanelSkeleton from '../../../../sidebar/my-top-10-games/MyTop10GamesPanelSkeleton';
import MyTop10GamesList from '../../../../sidebar/my-top-10-games/MyTop10GamesList';
import useRewardsTop10GamesData from './useRewardsTop10GamesData';
import {PDFPictureTrap} from '../export/PDFExportProvider';
import {Tooltip} from '../../../../common';
import {Payout} from '../../../../../types';
import {KeysMap} from '../components/chart/types';
import {REWARDS_DATA_COLORS, COMPACT_CURRENCY_FORMAT} from '../constants';

const ContentWrapper = styled.div`
    display: flex;
    flex-direction: column;
    margin-top: ${p => p.theme.muiTheme.spacing(10)}px;

    ${p => p.theme.muiTheme.breakpoints.up('flg')} {
        margin-top: 0;
    }
`;

const HeaderWrapper = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    margin-bottom: ${p => p.theme.muiTheme.spacing(4)}px;

    & h5 {
        font-size: 0.875rem;
        font-weight: 400;
        height: 1.25rem;

        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;

        color: ${p => p.theme.muiTheme.palette.text.primary};
    }

    ${p => p.theme.muiTheme.breakpoints.up('flg')} {
        & h5 {
            font-size: 1.12rem;
            font-weight: 300;
            height: 1.334em;
        }
    }

    ${p => p.theme.muiTheme.breakpoints.up('xl')} {
        & h5 {
            font-size: 1.38rem;
        }
    }
`;

const StyledPanel = withStyles<string, undefined, CardProps>(theme => ({
    root: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        minHeight: '600px',
        minWidth: '330px',
        padding: theme.spacing(3),
        backgroundColor: theme.palette.background.paper
    }
}))(Card);

const CODES = {
    title: 'epic.affiliate.fortnite_games.rewards.top_10_games.title',
    tooltip: 'epic.affiliate.fortnite_games.rewards.top_10_games.tooltip',
    gameTitle: 'epic.affiliate.fortnite_games.sidebar.my_top_10_games.game_title',
    category: 'epic.affiliate.fortnite_games.rewards.top_10_games.category',
    dataUnavailableTitle: 'epic.affiliate.fortnite_games.sidebar.data_unavailable_title',
    dataUnavailableDescription: 'epic.affiliate.fortnite_games.sidebar.data_unavailable_description'
};

export interface RewardsTop10GamesPanelProps {
    keysMap?: KeysMap;
    gamesLoading?: boolean;
}

const RewardsTop10GamesPanel: FC<RewardsTop10GamesPanelProps> = ({keysMap, gamesLoading}) => {
    const getMessage = useLocalization();
    const getComparisonColor = useComparisonColor();
    const locale = getLocale();

    const currencyFormatter = value =>
        new Intl.NumberFormat(locale, COMPACT_CURRENCY_FORMAT).format(value);

    const {data, loading} = useRewardsTop10GamesData();

    const list = useMemo(
        () =>
            data.map(({islandCode, ...rest}) => {
                const creativeIslandCode = String(islandCode);
                const game = keysMap[creativeIslandCode];
                const label = game ? String(game) : '';

                return {creativeIslandCode, label, ...rest};
            }),
        [data]
    );

    const barColor = getComparisonColor(REWARDS_DATA_COLORS[Payout.ENGAGEMENT]);

    return (
        <PDFPictureTrap id={getMessage(CODES.title)} index={2}>
            <ContentWrapper>
                <HeaderWrapper>
                    <Typography variant="h5">{getMessage(CODES.title)}</Typography>
                    <Tooltip placement="top" title={getMessage(CODES.tooltip)}>
                        <Box mx={1.5} display="flex" alignItems="center">
                            <Guideline />
                        </Box>
                    </Tooltip>
                </HeaderWrapper>

                <StyledPanel>
                    <Box display="flex" justifyContent="space-between" mx={3} mb={2}>
                        <Typography variant="body2" color="textSecondary">
                            {getMessage(CODES.gameTitle)}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                            {getMessage(CODES.category)}
                        </Typography>
                    </Box>

                    {loading || gamesLoading ? (
                        <MyTop10GamesPanelSkeleton />
                    ) : (
                        <MyTop10GamesList
                            list={list}
                            formatter={currencyFormatter}
                            tooltipTitle={getMessage(CODES.dataUnavailableTitle)}
                            tooltipDescription={getMessage(CODES.dataUnavailableDescription)}
                            color={barColor}
                        />
                    )}
                </StyledPanel>
            </ContentWrapper>
        </PDFPictureTrap>
    );
};

export default RewardsTop10GamesPanel;
